export const TaskAutomationTriggerType = {
  // Lead related triggers
  Lead: {
    Created: 'lead_created',
    StateChanged: 'lead_state_changed',
    Assigned: 'lead_assigned',
    Stale: 'stale_lead',
    Won: 'lead_won',
    Lost: 'lead_lost',
    Closed: 'lead_closed',
    CustomerFileUploaded: 'customer_file_uploaded',
  },
  // Meeting related triggers
  Meeting: {
    Scheduled: 'meeting_scheduled',
    Assigned: 'meeting_assigned',
    Cancelled: 'meeting_cancelled',
    CancelledByCustomer: 'meeting_cancelled_by_customer',
    CancelledByAgent: 'meeting_cancelled_by_agent',
    NoShowup: 'meeting_no_showup',
    Rescheduled: 'meeting_rescheduled',
    Over: 'meeting_over',
    OverbookingCreated: 'overbooking_created',
  },
  // Time-based triggers
  Time: {
    DaysPassed: 'days_passed',
  },
};

// Flat version of TaskAutomationTriggerType for backward compatibility
export const FlatTaskAutomationTriggerType = {
  LeadCreated: TaskAutomationTriggerType.Lead.Created,
  LeadStateChanged: TaskAutomationTriggerType.Lead.StateChanged,
  LeadAssigned: TaskAutomationTriggerType.Lead.Assigned,
  StaleLead: TaskAutomationTriggerType.Lead.Stale,
  LeadWon: TaskAutomationTriggerType.Lead.Won,
  LeadLost: TaskAutomationTriggerType.Lead.Lost,
  MeetingScheduled: TaskAutomationTriggerType.Meeting.Scheduled,
  MeetingCancelled: TaskAutomationTriggerType.Meeting.Cancelled,
  MeetingCancelledByCustomer: TaskAutomationTriggerType.Meeting.CancelledByCustomer,
  MeetingCancelledByAgent: TaskAutomationTriggerType.Meeting.CancelledByAgent,
  MeetingNoShowup: TaskAutomationTriggerType.Meeting.NoShowup,
  MeetingRescheduled: TaskAutomationTriggerType.Meeting.Rescheduled,
  MeetingOver: TaskAutomationTriggerType.Meeting.Over,
};

export const TaskAutomationCreateFor = {
  LeadAssignee: 'lead_assignee',
  BranchManager: 'branch_manager',
  BranchServiceManager: 'branch_service_manager',
  ServiceRequestAssignee: 'service_request_assignee',
  SpecifiedUsers: 'specified_users',
};

// Triggers rooted in the lead itself reject task_create_for=service_request_assignee (422),
// and only meeting-ish triggers carry a service request the "meeting" timing can reference.
export const LEAD_BASED_TRIGGER_TYPES = [
  TaskAutomationTriggerType.Lead.Created,
  TaskAutomationTriggerType.Lead.Assigned,
  TaskAutomationTriggerType.Lead.StateChanged,
  TaskAutomationTriggerType.Lead.Won,
  TaskAutomationTriggerType.Lead.Lost,
  TaskAutomationTriggerType.Lead.Closed,
  TaskAutomationTriggerType.Lead.Stale,
  TaskAutomationTriggerType.Lead.CustomerFileUploaded,
];

export const MEETING_REFERENCE_TRIGGER_TYPES = [
  ...Object.values(TaskAutomationTriggerType.Meeting),
  TaskAutomationTriggerType.Lead.CustomerFileUploaded,
];

export const TaskAutomationConsequenceType = {
  AddTaskNote: 'add_task_note',
  ChangeLeadState: 'change_lead_state',
  SetExtraField: 'set_extra_field',
  TaskDone: 'task_done',
  TaskRetired: 'task_retired',
  SnoozeForDays: 'snooze_for_days',
  OpenAppointment: 'open_appointment',
  OpenAddComment: 'open_add_comment',
  OpenComposeEmail: 'open_compose_email',
};

// Consequences the server does not execute — the task UI opens the matching
// dialog itself when the action button is clicked.
export const UI_HANDLED_CONSEQUENCE_TYPES = [
  TaskAutomationConsequenceType.OpenAppointment,
  TaskAutomationConsequenceType.OpenAddComment,
  TaskAutomationConsequenceType.OpenComposeEmail,
];

// The retire dropdown is a strict subset of the trigger list (plus the
// synthetic client-side days_passed entry).
export const RETIRE_TRIGGER_TYPES = {
  Lead: ['lead_won', 'lead_lost', 'lead_closed', 'lead_state_changed'],
  Meeting: [
    'meeting_scheduled',
    'meeting_assigned',
    'meeting_cancelled',
    'meeting_cancelled_by_customer',
    'meeting_cancelled_by_agent',
    'meeting_rescheduled',
    'meeting_over',
    'overbooking_created',
  ],
};

export const COMPOSE_EMAIL_PLACEHOLDERS = ['{firstname}', '{lastname}', '{email}', '{phone}', '{assignee}'];

export const TaskAutomationReferenceType = {
  Trigger: 'trigger',
  Meeting: 'meeting',
};

export const TaskAutomationButtonType = {
  Normal: 'normal',
  Solid: 'solid',
  Blue: 'blue',
  Filled: 'filled',
  Danger: 'danger',
  Text: 'text',
  Link: 'link',
};

export const TaskAutomationFormStepKey = {
  BasicInfo: 0,
  Trigger: 1,
  TaskSetup: 2,
  Actions: 3,
  RetireTriggers: 4,
  Review: 5,
};

export const TASK_AUTOMATION_DEFAULT_MESSAGES = {
  // Lead triggers
  [`trigger.${TaskAutomationTriggerType.Lead.Created}`]: 'Lead Created',
  [`trigger.${TaskAutomationTriggerType.Lead.StateChanged}`]: 'Lead State Changed',
  [`trigger.${TaskAutomationTriggerType.Lead.Assigned}`]: 'Lead Assigned',
  [`trigger.${TaskAutomationTriggerType.Lead.Stale}`]: 'Project Idle',
  [`trigger.${TaskAutomationTriggerType.Lead.Won}`]: 'Lead Won',
  [`trigger.${TaskAutomationTriggerType.Lead.Lost}`]: 'Lead Lost',

  // Meeting triggers
  [`trigger.${TaskAutomationTriggerType.Meeting.Scheduled}`]: 'Meeting Scheduled',
  [`trigger.${TaskAutomationTriggerType.Meeting.Cancelled}`]: 'Meeting Cancelled',
  [`trigger.${TaskAutomationTriggerType.Meeting.CancelledByCustomer}`]: 'Cancelled by Customer',
  [`trigger.${TaskAutomationTriggerType.Meeting.CancelledByAgent}`]: 'Cancelled by Agent',
  [`trigger.${TaskAutomationTriggerType.Meeting.NoShowup}`]: 'No Show',
  [`trigger.${TaskAutomationTriggerType.Meeting.Rescheduled}`]: 'Rescheduled',
  [`trigger.${TaskAutomationTriggerType.Meeting.Over}`]: 'Meeting Over',
  [`trigger.${TaskAutomationTriggerType.Meeting.Assigned}`]: 'Meeting Assigned',
  [`trigger.${TaskAutomationTriggerType.Meeting.OverbookingCreated}`]: 'Overbooking Created',
  [`trigger.${TaskAutomationTriggerType.Lead.Closed}`]: 'Lead Closed',
  [`trigger.${TaskAutomationTriggerType.Lead.CustomerFileUploaded}`]: 'Customer File Uploaded',
  [`retire_trigger.${TaskAutomationTriggerType.Meeting.Assigned}`]: 'Meeting Assigned',
  [`retire_trigger.${TaskAutomationTriggerType.Meeting.OverbookingCreated}`]: 'Overbooking Created',
  [`retire_trigger.${TaskAutomationTriggerType.Lead.Closed}`]: 'Lead Closed',

  // For backward compatibility
  [`trigger.${FlatTaskAutomationTriggerType.LeadCreated}`]: 'Lead Created',
  [`trigger.${FlatTaskAutomationTriggerType.LeadStateChanged}`]: 'Lead State Changed',
  [`trigger.${FlatTaskAutomationTriggerType.LeadAssigned}`]: 'Lead Assigned',
  [`trigger.${FlatTaskAutomationTriggerType.MeetingScheduled}`]: 'Meeting Scheduled',
  [`trigger.${FlatTaskAutomationTriggerType.MeetingCancelledByCustomer}`]: 'Cancelled by Customer',
  [`trigger.${FlatTaskAutomationTriggerType.MeetingCancelled}`]: 'Meeting Cancelled',
  [`trigger.${FlatTaskAutomationTriggerType.MeetingCancelledByAgent}`]: 'Cancelled by Agent',
  [`trigger.${FlatTaskAutomationTriggerType.MeetingNoShowup}`]: 'No Show',
  [`trigger.${FlatTaskAutomationTriggerType.MeetingRescheduled}`]: 'Rescheduled',
  [`trigger.${FlatTaskAutomationTriggerType.MeetingOver}`]: 'Meeting Over',
  [`trigger.${FlatTaskAutomationTriggerType.StaleLead}`]: 'Project Idle',
  [`trigger.${FlatTaskAutomationTriggerType.LeadWon}`]: 'Lead Won',
  [`trigger.${FlatTaskAutomationTriggerType.LeadLost}`]: 'Lead Lost',

  // Retire trigger messages for Lead
  [`retire_trigger.${TaskAutomationTriggerType.Lead.Created}`]: 'Lead Created',
  [`retire_trigger.${TaskAutomationTriggerType.Lead.StateChanged}`]: 'Lead State Changed',
  [`retire_trigger.${TaskAutomationTriggerType.Lead.Assigned}`]: 'Lead Assigned',
  [`retire_trigger.${TaskAutomationTriggerType.Lead.Stale}`]: 'Project Idle',
  [`retire_trigger.${TaskAutomationTriggerType.Lead.Won}`]: 'Lead Won',
  [`retire_trigger.${TaskAutomationTriggerType.Lead.Lost}`]: 'Lead Lost',

  // Retire trigger messages for Meeting
  [`retire_trigger.${TaskAutomationTriggerType.Meeting.Scheduled}`]: 'Meeting Scheduled',
  [`retire_trigger.${TaskAutomationTriggerType.Meeting.Cancelled}`]: 'Meeting Cancelled',
  [`retire_trigger.${TaskAutomationTriggerType.Meeting.CancelledByCustomer}`]: 'Cancelled by Customer',
  [`retire_trigger.${TaskAutomationTriggerType.Meeting.CancelledByAgent}`]: 'Cancelled by Agent',
  [`retire_trigger.${TaskAutomationTriggerType.Meeting.NoShowup}`]: 'No Show',
  [`retire_trigger.${TaskAutomationTriggerType.Meeting.Rescheduled}`]: 'Rescheduled',
  [`retire_trigger.${TaskAutomationTriggerType.Meeting.Over}`]: 'Meeting Over',

  // Retire trigger messages for Time
  [`retire_trigger.${TaskAutomationTriggerType.Time.DaysPassed}`]: 'Days Passed',

  [`create_for.${TaskAutomationCreateFor.LeadAssignee}`]: 'Lead Assignee',
  [`create_for.${TaskAutomationCreateFor.BranchManager}`]: 'Branch Manager',
  [`create_for.${TaskAutomationCreateFor.BranchServiceManager}`]: 'Branch Service Manager',
  [`create_for.${TaskAutomationCreateFor.ServiceRequestAssignee}`]: 'Service Request Assignee',
  [`create_for.${TaskAutomationCreateFor.SpecifiedUsers}`]: 'Specific Users',

  [`reference.${TaskAutomationReferenceType.Trigger}`]: 'Created',
  [`reference.${TaskAutomationReferenceType.Meeting}`]: 'Meeting',

  [`consequence.${TaskAutomationConsequenceType.AddTaskNote}`]: 'Add Task Note',
  [`consequence.${TaskAutomationConsequenceType.ChangeLeadState}`]: 'Change Lead State',
  [`consequence.${TaskAutomationConsequenceType.SetExtraField}`]: 'Set Extra Field',
  [`consequence.${TaskAutomationConsequenceType.TaskDone}`]: 'Task Done',
  [`consequence.${TaskAutomationConsequenceType.TaskRetired}`]: 'Task Retired',
  [`consequence.${TaskAutomationConsequenceType.SnoozeForDays}`]: 'Snooze Task',
  [`consequence.${TaskAutomationConsequenceType.OpenAppointment}`]: 'Open New Appointment',
  [`consequence.${TaskAutomationConsequenceType.OpenAddComment}`]: 'Open Comment Box',
  [`consequence.${TaskAutomationConsequenceType.OpenComposeEmail}`]: 'Open Email Composer',

  'retire_trigger.group_time': 'Time',
  'retire_trigger.group_lead': 'Lead',
  'retire_trigger.group_meeting': 'Meeting',

  'step.basic_info': 'Basics',
  'step.trigger': 'Trigger',
  'step.task_setup': 'Task',
  'step.actions': 'Actions',
  'step.retire_triggers': 'Retire',
  'step.review': 'Review',

  'form.name': 'Automation Name',
  'form.description': 'Description',
  'form.lead_source': 'Lead Source',
  'form.active': 'Active',
  'form.trigger_type': 'Automatically Create Task on',
  'form.retire_trigger_type': 'Retire the created task on',
  'form.target_lead_state_hash_id': 'to State',
  'form.source_lead_state_hash_id': 'From State',
  'form.stale_days': 'for',
  'form.stale_lead_state_id': 'When the Lead stays in',
  'form.task_title': 'Task Title',
  'form.task_description': 'Task Description',
  'form.task_create_for': 'Create Task for',
  'form.due_after': 'Due After (minutes)',
  'form.task_due_time': 'Task Due Time',
  'form.custom_due_after': 'Custom Due After (minutes)',
  'form.retire_after': 'Retires after passing',
  'form.button': 'Button',
  'form.button_label': 'Button Label',
  'form.button_type': 'Button Type',
  'form.button_preview': 'Button Preview',

  [`button_type.${TaskAutomationButtonType.Normal}`]: 'Normal',
  [`button_type.${TaskAutomationButtonType.Solid}`]: 'Solid',
  [`button_type.${TaskAutomationButtonType.Blue}`]: 'Blue',
  [`button_type.${TaskAutomationButtonType.Filled}`]: 'Filled',
  [`button_type.${TaskAutomationButtonType.Danger}`]: 'Danger',
  [`button_type.${TaskAutomationButtonType.Text}`]: 'Text',
  [`button_type.${TaskAutomationButtonType.Link}`]: 'Link',
  'form.consequence_type': 'Perform',
  'form.meta': '',
  'form.remove_consequence': 'Remove Event',
  'form.add_consequence': 'Add on Button Click Event',
  'form.remove_button': 'Remove Button',
  'form.add_button': 'Add Action Button',
  'form.retire_trigger': 'Retire Trigger',
  'form.trigger_meta': 'Trigger Meta (JSON)',
  'form.remove_trigger': 'Remove',
  'form.add_retire_trigger': 'Add Retire Trigger',
  'form.previous': 'Previous',
  'form.next': 'Next',
  'form.submit': 'Submit',
  'form.lead_state': 'To State',
  'form.note': 'Note',
  'form.extra_field_type': 'Extra Field Type',
  'form.value': 'Value',
  'form.task_due_in_minutes_after_reference': 'Due',
  'form.due_minutes_after': 'Minutes after',
  'form.due_hours_after': 'Hours after',
  'form.due_days_after': 'Days after',
  'form.due_minutes_before': 'Minutes before',
  'form.due_hours_before': 'Hours before',
  'form.due_days_before': 'Days before',
  'form.validation_error': 'Please fix the form before proceeding',

  'form.users': 'Users',
  'form.branches': 'Branches',
  'form.branch_services': 'Branch Services',
  'form.scoping_hint': 'Leave empty to apply to all branches and services',
  'form.scoping_required': 'At least one branch or branch service is required.',
  'form.automated_actions_hint': 'These events run on their own the moment the task is created, so nobody has to click anything. Add events with the + next to this node; use a button for anything that should wait for someone.',
  'form.action_all_automatic': 'Runs automatically',
  'form.consider_higher_states': 'Also match higher states',
  'form.snooze_days': 'Snooze for',
  'form.comment': 'Comment',
  'form.email_subject': 'Subject',
  'form.email_text': 'Email Text',
  'form.insert_placeholder': 'Insert placeholder',
  'form.ui_handled_hint': 'Opens this dialog for the agent when the button is clicked — nothing happens on the server.',
  'form.specified_users_required': 'Select at least one user',
  'form.scope': 'Scope & source',
  'form.scope_all': 'Applies everywhere',
  'form.scope_selected': '{count} selected',
  'canvas.event_position': 'Event {index} of {count}',

  'conditions.title': 'Conditions',
  'conditions.empty': 'No conditions — applies to any lead',
  'conditions.all_must_match': 'All conditions must match',
  'conditions.add_condition': 'Add condition',
  'conditions.field': 'Field',
  'conditions.operator': 'Operator',
  'conditions.value': 'Value',
  'conditions.extra_field': 'Extra Field',
  'conditions.count': '{count} conditions',
  'conditions.count_one': '1 condition',
  'conditions.unavailable': 'Conditions cannot be edited right now — existing conditions are preserved.',
  'conditions.duration_unit_minutes': 'Minutes',
  'conditions.duration_unit_hours': 'Hours',
  'conditions.duration_unit_days': 'Days',

  'operator.equals': 'equals',
  'operator.not_equals': 'does not equal',
  'operator.greater_than': 'greater than',
  'operator.greater_than_or_equal': 'greater than or equal',
  'operator.less_than': 'less than',
  'operator.less_than_or_equal': 'less than or equal',
  'operator.in': 'is any of',
  'operator.not_in': 'is none of',
  'operator.exists': 'exists',
  'operator.contains': 'contains',
  'operator.not_contains': 'does not contain',
  'operator.starts_with': 'starts with',
  'operator.not_starts_with': 'does not start with',

  'canvas.settings': 'Settings',
  'canvas.trigger_node': 'Trigger',
  'canvas.conditions_node': 'Conditions',
  'canvas.task_node': 'Task',
  'canvas.action_node': 'Action',
  'canvas.automated_node': 'Automated',
  'canvas.automated_title': 'Automated actions',
  'canvas.automated_summary': 'Run when the task is created',
  'canvas.consequence_node': 'Event',
  'canvas.retire_node': 'Retire',
  'canvas.add_action': 'Add action',
  'canvas.add_consequence': 'Add event',
  'canvas.add_retire': 'Add retire trigger',
  'canvas.fix_errors_to_save': 'Fix the highlighted issues to save',
  'canvas.read_only_notice': 'You cannot edit this automation',
  'canvas.select_node_hint': 'Select an element on the canvas to edit it',
  'canvas.no_due_date': 'Due immediately',
  'canvas.save': 'Save',
  'canvas.name_your_automation': 'Name your automation',
  'canvas.delete_node': 'Delete',
  'canvas.delete_node_confirmation': 'Remove this element?',
  'canvas.due_summary': 'Due {days, plural, one {# day} other {# days}} after {reference}',
  'canvas.due_summary_hours': 'Due {hours, plural, one {# hour} other {# hours}} after {reference}',
  'canvas.due_summary_before': 'Due {days, plural, one {# day} other {# days}} before {reference}',
  'canvas.due_summary_hours_before': 'Due {hours, plural, one {# hour} other {# hours}} before {reference}',
  'canvas.due_summary_minutes': 'Due {minutes, plural, one {# minute} other {# minutes}} after {reference}',
  'canvas.due_summary_minutes_before': 'Due {minutes, plural, one {# minute} other {# minutes}} before {reference}',
  'canvas.users_count': '{count} users',
  'canvas.events_count': '{count} events',
  'canvas.events_count_one': '1 event',
  'canvas.more_conditions': '+{count} more',

  'retire_trigger.group_other': 'Other',

  'summary.empty': 'Pick a trigger to see a summary of this automation.',
  'summary.when': 'When {trigger}, ',
  'summary.conditions_one': 'if 1 condition matches, ',
  'summary.conditions': 'if {count} conditions match, ',
  'summary.task': 'create the task {title} for {createFor}, due {days, plural, one {# day} other {# days}} after {reference}.',
  'summary.task_hours': 'create the task {title} for {createFor}, due {hours, plural, one {# hour} other {# hours}} after {reference}.',
  'summary.task_before': 'create the task {title} for {createFor}, due {days, plural, one {# day} other {# days}} before {reference}.',
  'summary.task_hours_before': 'create the task {title} for {createFor}, due {hours, plural, one {# hour} other {# hours}} before {reference}.',
  'summary.task_minutes': 'create the task {title} for {createFor}, due {minutes, plural, one {# minute} other {# minutes}} after {reference}.',
  'summary.task_minutes_before': 'create the task {title} for {createFor}, due {minutes, plural, one {# minute} other {# minutes}} before {reference}.',
  'summary.actions': 'Buttons: {buttons}.',
  'summary.automated': 'On creation it runs {events} automatically.',
  'summary.retire': 'The task is retired when {conditions}.',
  'summary.retire_after_days': 'after {days} days',
  'summary.or': ' or ',
  'operator.not_exists': 'is empty',

  'condition_field.order_volume': 'Order Volume',
  'condition_field.branch_service': 'Branch Service',
  'condition_field.priority': 'Priority',
  'condition_field.assignee': 'Assignee',
  'condition_field.creation_date': 'Creation Date',
  'condition_field.time_to_next_service_request': 'Time to next appointment',
  'condition_field.time_from_previous_service_request': 'Time since last appointment',

  'error.name_required': 'Name is required',
  'error.scoping_required': 'At least one branch or branch service is required',
  'error.trigger_required': 'Select a trigger',
  'error.lead_state_one_required': 'Select at least one of the two states',
  'error.lead_states_same': 'The two states cannot be the same',
  'error.stale_lead_state_required': 'Select a state',
  'error.stale_days_required': 'Enter the number of days',
  'error.task_title_required': 'Task title is required',
  'error.task_create_for_required': 'Select who the task is created for',
  'error.create_for_not_allowed': 'This assignment is not available for the selected trigger',
  'error.meeting_timing_not_allowed': 'Meeting-based timing is not available for the selected trigger',
  'error.specified_users_required': 'Select at least one user',
  'error.button_label_required': 'Button label is required',
  'error.consequences_required': 'Add at least one event',
  'error.consequence_type_required': 'Select an event type',
  'error.condition_value_required': 'Give every condition a value',
  'error.consequence_not_automatable': 'This event opens a dialog, so it can only run from a button',
  'error.note_required': 'Note is required',
  'error.reason_required': 'Reason is required',
  'error.lead_state_required': 'Select a state',
  'error.extra_field_type_required': 'Select an extra field',
  'error.extra_field_value_required': 'Value is required',
  'error.snooze_days_required': 'Enter the number of days',
  'error.comment_required': 'Comment is required',
  'error.email_text_required': 'Email text is required',
  'error.retire_trigger_type_required': 'Select a retire trigger',
  'error.days_passed_required': 'Enter the number of days',

  'preview.caption': 'Task preview',
  'preview.untitled': 'Untitled task',

  'review.due_days_after': 'Due {days} days after {reference}',
  'review.action_fallback': 'Action {index}',
  'review.auto_retire': 'Auto-retire',
  'review.additional_triggers': 'Additional triggers ({count}):',
  'review.duration_days': 'Days',
  'review.days_parentheses': '({days} days)',
};
